// =============================================================================
// Compliance + HACCP dual-run glue (Phase 3, module 2) — sibling of
// selloutSync.js and same contract: every recorder is BEST-EFFORT, swallows its
// own errors, and can NEVER throw into the live kiosk path. The Apps Script
// writes stay exactly as they are — still the source of truth (and for HACCP,
// still the legal record until cut-over).
//
// The pleasant surprise of this module: the app already funnels every
// compliance write through ONE helper (cmpComplianceLog) plus the HACCP pair
// (cmpHaccpEvent / cmpHaccpSign), so the shadow is three recorders, not 21
// wirings. The wiring session calls each recorder from the .then() of the old
// backend fetch — AFTER the old write resolves — because the old backend MINTS
// the incident/batch ids (incidentId, batchId, correctiveActionId) in its
// response, and those ids are how the new side's case rows stay matched to the
// old sheet during dual-run (compliance_cases.legacy_ref). Old write failed =
// nothing happened = no shadow, which is exactly right while the sheet is boss.
//
// Three record families (the 0009 schema derives the same split, in one place):
//   tick      one row per action; idem key = the natural cell address
//   periodic  latest-wins state per period; idem key = the period identity
//   case      compliance_cases row (state) + every step in compliance_events
//             (log). Raise events OPEN a case; close/resolve events flip it.
//             Follow events link by the old backend's incidentId; if the case
//             isn't found (raised before the shadow existed) the event lands
//             with case_id null and the reconcile flags the orphan — the DB
//             never rejects it.
//
// testMode (the app's QA flag) is passed through by the caller: it lands in
// the test_mode COLUMN and prefixes the idem key with `qa|`, so QA runs can
// never collide with — or pollute — real rows. Photos stay on Google Drive
// during dual-run: any Drive URL the form attaches simply rides inside
// `payload`, no bytes move.
//
// Shared plumbing (host gate, site-id cache, idem, best-effort wrapper, PIN
// pass) is imported from stockSync so there is exactly ONE ?dualrun switch and
// ONE gate across all modules. Rollout is kitchen-only in practice — it is the
// only live compliance writer today; the shops' tiles are coming-soon.
// NOTE for the wiring session: the kitchen app sends site "mcr" and the old
// backend normalises it to "kitchen" — confirm which key the sites table uses
// before go-live; siteIdForKey() takes the key verbatim.
//
// The planners (planComplianceRow / planHaccpRow / cellDate) are PURE and
// exported so the node selftest can prove key shapes without a database.
// =============================================================================
import { supabase } from "./supabaseClient.js";
import { dualRunEnabled, siteIdForKey, shadow, idem, ensurePass } from "./stockSync.js";
// Borrowed rather than copied: normalizeHost is the hardened host matcher added by
// the #351 review (it folds away a stray protocol, port, trailing slash or capital
// so a mis-pasted host at flip time can never silently fail to match). Duplicating a
// security-relevant matcher is how the two copies drift apart.
import { normalizeHost } from "./complianceReader.js";
// The Set itself (Lane CO, 16 Sep 2026): moved to its own import-free file so
// the daily lock-gate check (tools/lane_co_compliance_lock_gate.mjs) can read
// the same list in Node without pulling in this file's Supabase client. See
// complianceDoorSet.js for why. Contents unchanged — still empty.
import { COMPLIANCE_CASE_DOOR_HOSTS } from "./complianceDoorSet.js";
// The estate's existing "that didn't save — try again" banner. Reused, not
// re-invented: it is the one failure alert in this repo that a library can call
// without a screen handing it a setState, it already carries the brand styling
// and `role="alert"`, and confirmedWrite.js has no imports of its own so there is
// no cycle and no bundle growth (boh-kitchen.jsx:10 and foh.jsx:14 already pull
// this file in). window.alert() was the other candidate — the kitchen uses it for
// action failures — and was rejected: these calls run in the background behind a
// form submit, and a blocking modal that stops the whole tablet mid-shift is a
// new failure mode on a live food-safety path.
import { notifyWriteFailed } from "./confirmedWrite.js";

// =============================================================================
// The case-door gate (migration 1440). EMPTY, so nothing changes yet.
//
// WHY IT EXISTS, in plain English. Migration 1441 stops kiosks reading the
// food-safety incidents table directly — today anyone with the public key can
// read every shop's legal record. But this file's case helpers currently need
// that read: raising an incident writes a row and then reads its id straight
// back, and closing one looks the incident up by id. Measured on staging
// (15 Sep 2026): with the table locked, a raise is REFUSED OUTRIGHT (the
// incident is never recorded at all) and a close SILENTLY matches zero rows,
// leaving the incident open for ever with nothing in any log to say so.
//
// So 1440 adds three doors that do the work inside the database and hand back
// just the id. This gate decides which route this file takes. It ships EMPTY:
// every kiosk keeps using today's direct table calls, byte for byte, until a
// host is added here in daylight — one host at a time, after 1440 is on
// production. `?compcasedoor=off` is the instant per-tablet kill switch and
// always wins.
//
// The flip ORDER matters and it is the opposite of a read flip: this gate goes
// ON first and soaks, and only then may 1441 be merged. Merging 1441 while any
// kiosk is still on the table route would break that kitchen's incident log.
// =============================================================================
export { COMPLIANCE_CASE_DOOR_HOSTS };

// Parse the ?compcasedoor override: "on" | "off" | null. Pure (search injectable
// for tests; falls back to window.location.search in the browser).
export function caseDoorOverride(search) {
  try {
    const s = search != null ? search : (typeof window !== "undefined" ? window.location.search : "");
    const q = new URLSearchParams(s).get("compcasedoor");
    if (q === "on" || q === "off") return q;
  } catch (e) {}
  return null;
}

// The PURE decision — every input explicit, no globals, so the whole truth table
// is unit-tested with no window. `off` always wins; otherwise a listed host (or
// an explicit `on`) takes the door and everything else stays on today's route.
export function planCaseWrite({ hostListed = false, override = null } = {}) {
  if (override === "off") return { useDoor: false, route: "table" };   // instant kill — always wins
  if (override === "on" || hostListed) return { useDoor: true, route: "door" };
  return { useDoor: false, route: "table" };
}

// Runtime combiner the three case helpers call.
export function caseDoorEnabled(search) {
  let hostListed = false;
  if (typeof window !== "undefined") {
    const current = normalizeHost(window.location.hostname);
    if (current) {
      for (const h of COMPLIANCE_CASE_DOOR_HOSTS) {
        if (normalizeHost(h) === current) { hostListed = true; break; }
      }
    }
  }
  return planCaseWrite({ hostListed, override: caseDoorOverride(search) }).useDoor;
}

// Fresh at CALL time, never from React state — the day-rollover lesson
// (2026-05-15): a kiosk that slept through midnight must stamp the new day.
const londonToday = () => new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" });
const londonNowHM = () => new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" });

// QA runs land under a distinct key namespace as well as the test_mode column,
// so a QA double-tap collapses against QA rows, never against real ones.
export function qaKey(testMode, ...parts) {
  return testMode ? idem("qa", ...parts) : idem(...parts);
}

// The cleaning grid's cell day: weekStart (a Monday) + dayIndex. TZ-safe local
// parse (the cmpMondayOf lesson) — "2026-07-06" must never slip a day through
// a UTC round-trip.
export function cellDate(weekStart, dayIndex) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(weekStart || ""));
  if (!m) return londonToday();
  const d = new Date(+m[1], +m[2] - 1, +m[3] + (Number(dayIndex) || 0));
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
}

// --- the event -> row planner (pure) ------------------------------------------
// Decides, per event: the entry_date the record BELONGS to (the cell's day,
// the sweep's Monday — not the write time), the idem key (retries collapse;
// legal repeats carry a London HH:MM the way sell-outs carry the picked time),
// the case work to do, and the old backend's id to keep as legacy_ref.
// Unknown events still produce a plan — the 0009 check constraint rejects the
// row, the recorder logs + swallows, and the gap surfaces in the console and
// the reconcile, never on the kiosk.
export function planComplianceRow({ siteKey, event, payload = {}, testMode = false, response = null, day = londonToday(), hm = londonNowHM() }) {
  const p = payload || {};
  const r = response || {};
  const k = (...parts) => qaKey(testMode, siteKey, "cmp", event, ...parts);
  let entryDate = day, idemKey, casePlan = null, autoCorrective = null, legacyRef = null;

  switch (event) {
    // -- ticks --
    case "cleaning-cell":
      entryDate = cellDate(p.weekStart, p.dayIndex);
      // state is part of the identity: done -> removed -> re-done each lands
      // (a same-state repeat collapses — rare, and derived cell state is
      // identical; the reconcile folds same-state repeats on the old side).
      idemKey = k(p.taskId != null ? p.taskId : p.taskIndex, entryDate, p.state);
      break;
    case "pest-daily":
      // several rows per area per day are legal; the write-minute separates
      // them and collapses a double-tap, same trick as sell-out times.
      idemKey = k(p.area, day, hm);
      if (r.incidentId) autoCorrective = { caseRef: r.incidentId, description: p.details || ("Pest evidence — " + (p.area || "?")) };
      break;
    case "pest-deep-sweep-tick":
      entryDate = p.weekStart || day;
      idemKey = k(p.taskNumber, p.weekStart);
      break;

    // -- periodic sign-offs --
    case "cleaning-weekly-signoff":
      entryDate = p.weekStart || day;
      idemKey = k(p.weekStart, day); // latest-wins per week; a next-day re-sign lands as its own row
      break;
    case "pest-deep-sweep-submit":
      entryDate = p.weekStart || day;
      idemKey = k(p.weekStart, day);
      if (p.correctiveNeeded && r.incidentId) autoCorrective = { caseRef: r.incidentId, description: p.correctiveText || "Deep sweep corrective" };
      break;
    case "probe-calibration":
      idemKey = k(p.probeId, day, hm); // re-tests same day are legal (dropped probe)
      if (r.incidentId) autoCorrective = { caseRef: r.incidentId, description: p.action || ("Probe out of tolerance — " + (p.probeName || p.probeId || "?")) };
      break;
    case "fish-freezing-intake":
      legacyRef = r.batchId || null; // the old backend mints the batch id
      idemKey = k(legacyRef || idem(p.species, p.supplier, p.batchRef, day, hm));
      break;
    case "fish-freezing-out":
      idemKey = k(p.batchId); // one out per batch
      if (r.correctiveActionId) autoCorrective = { caseRef: r.correctiveActionId, description: p.actionNotes || ("Fish core temp out of spec — " + (p.batchId || "?")) };
      break;
    case "declaration-sign":
      idemKey = k(p.documentType, day, hm); // signer identity comes from the stamped staff_id, never the key
      break;

    // -- case lifecycles --
    case "emergency-fridge-raise":
      legacyRef = r.incidentId || null;
      idemKey = k(legacyRef || idem(p.unit, day, hm));
      casePlan = {
        op: "open", kind: "emergency_fridge", caseRef: legacyRef,
        description: (p.unit || "?") + " — failed " + (p.failureTime || "?") + (p.initialTemp != null && p.initialTemp !== "" ? " at " + p.initialTemp + "°C" : ""),
        detail: { unit: p.unit, failureTime: p.failureTime, initialTemp: p.initialTemp },
      };
      if (r.correctiveActionId) autoCorrective = { caseRef: r.correctiveActionId, description: "Emergency fridge incident — " + (p.unit || "?") };
      break;
    case "emergency-fridge-check":
      legacyRef = p.incidentId || null;
      idemKey = k(p.incidentId, day, hm); // 2-hourly checks repeat all day
      casePlan = { op: "follow", caseRef: p.incidentId };
      break;
    case "emergency-fridge-disposal":
      legacyRef = p.incidentId || null;
      idemKey = k(p.incidentId); // the 4-hour-rule disposal happens once
      casePlan = { op: "follow", caseRef: p.incidentId };
      break;
    case "emergency-fridge-close":
      legacyRef = p.incidentId || null;
      idemKey = k(p.incidentId);
      casePlan = { op: "resolve", caseRef: p.incidentId, resolution: p.actionNotes || "closed — fridge back in service" };
      break;
    case "corrective-raise":
      legacyRef = r.incidentId || null;
      idemKey = k(legacyRef || idem(day, hm));
      casePlan = {
        op: "open", kind: "corrective", caseRef: legacyRef,
        description: p.description || "", detail: { action: p.action, source: "ad hoc raise" },
      };
      break;
    case "corrective-resolve":
      legacyRef = p.incidentId || null;
      idemKey = k(p.incidentId);
      casePlan = { op: "resolve", caseRef: p.incidentId, resolution: p.resolutionAction || "" };
      break;

    default:
      idemKey = k(day, hm);
  }
  return { entryDate, idemKey, casePlan, autoCorrective, legacyRef };
}

// --- the HACCP cell planner (pure) ---------------------------------------------
// entry_date = the sheet day the app sends (device-local today); the section
// decides what disambiguates repeats: opening/closing ticks and pH batches are
// one-per-cell (re-tick collapses, harmless — the value is always `true` /
// batch-keyed), storage temps may be re-probed, deliveries and cook batches
// repeat all day.
export function planHaccpRow({ siteKey, section, field, value, testMode = false, date, hm = londonNowHM() }) {
  const entryDate = date || londonToday();
  const v = value && typeof value === "object" ? value : {};
  const disc =
    section === "storageTemp" ? [hm]
    : section === "delivery"  ? [v.supplier, v.invoice, hm]
    : section === "cooking"   ? [v.item, hm]
    : []; // opening / closing / ricePh: the field IS the cell address
  return { entryDate, idemKey: qaKey(testMode, siteKey, "haccp", section, field, entryDate, ...disc) };
}

// --- common preamble: gate -> per-action PIN pass -> site uuid -> best-effort ---
async function shadowWrite(label, { staffPin, siteKey }, fn) {
  if (!dualRunEnabled()) return { ok: true, skipped: true };
  try {
    const pass = await ensurePass(staffPin, siteKey);
    if (!pass?.ok) { console.warn(`[compliance-sync] ${label} — no pass, shadow skipped`); return { ok: false, error: "no pass" }; }
    const siteId = await siteIdForKey(siteKey);
    if (!siteId) return { ok: false, error: "no site id for " + siteKey };
    return await shadow(label, () => fn(siteId));
  } catch (e) {
    console.warn(`[compliance-sync] ${label} — shadow threw:`, e?.message || e);
    return { ok: false, error: e };
  }
}

// --- case helpers (each best-effort inside the shadow) ---------------------------

// "The door isn't on this database yet." Same test deliveryOrdersSync.js and
// incidentsSync.js already use, because it is the one PostgREST actually gives:
// a missing function comes back as PGRST202 "Could not find the function … in
// the schema cache", NOT as a Postgres 42883. Measured over the wire against
// staging, 15 Sep 2026.
//
// Why it is worth a line of its own: if a host is added to the set below BEFORE
// migration 1440 is applied to production, every corrective and emergency-fridge
// raise from that tablet quietly stops being recorded — and in the console it
// would look exactly like an expired staff pass. This makes the two distinct, so
// the first flipped tablet says what is wrong instead of just going quiet.
const caseDoorNotThere = (e) =>
  e?.code === "PGRST202" || /could not find the function/i.test(String(e?.message || ""));

// What to shout when a door says no. Exported so the selftest can pin it.
export function describeCaseDoorError(fnName, error) {
  return "[compliance-sync] case door " + fnName + " " +
    (caseDoorNotThere(error)
      ? "IS NOT ON THIS DATABASE (PostgREST PGRST202) — migration 1440 has not been applied here, " +
        "so this incident was NOT recorded on the live system. Take this host off COMPLIANCE_CASE_DOOR_HOSTS " +
        "(or add ?compcasedoor=off) until 1440 is applied."
      : "rejected the call") +
    ": " + ((error && (error.message || error.details)) || String(error == null ? "" : error));
}
// The stable tag every door failure carries, so a console filter / log sweep can
// find them all with one string no matter which door failed or why. Never change
// it without changing whatever is searching for it.
export const CASE_DOOR_FAILURE_TAG = "compliance_case_door_failure";

// What the KITCHEN sees, in plain English, with something to actually do next.
// Deliberately short — the engineering detail goes to the console, not to a
// banner over somebody's fridge form. Pure + exported so the test can pin it.
//
// It does NOT say "the incident was lost", because during dual-run Google is
// still the record and this module is only the shadow of that write: telling a
// chef their emergency-fridge incident had vanished would get it raised twice in
// the system that actually counts. It says what is true — the copy did not save,
// do the step again.
export function caseDoorFailureText(fnName, error) {
  const what =
    fnName === "compliance_case_ensure" ? "that incident didn't save to the new system"
    : fnName === "compliance_case_resolve_by_ref" ? "closing that incident didn't save to the new system"
    : "couldn't look that incident up in the new system";
  return "Food safety: " + what + " — try that step again."
    + (caseDoorNotThere(error) ? " This tablet is set up wrong — tell the office." : "");
}

// The banner auto-clears after 6s and they stack on top of each other, so a burst
// of the same failure would be an unreadable pile. Same message inside that
// window = one banner. The console line is NEVER suppressed.
let _lastCaseDoorAlert = { msg: "", at: 0 };

// NEVER SWALLOW ON THE ON PATH (external audit, 15 Sep 2026). Whatever the door
// said no to — an expired staff pass (42501), a tablet bound to another shop
// (42501), a migration that is not applied here (PGRST202), a dropped connection
// — it is surfaced twice: once to the console with a stable tag for whoever is
// watching the flip, and once on the tablet's own screen for the person standing
// in front of it. The earlier version console.warn'd some of these, which on a
// kiosk with no console open is the same as saying nothing at all.
export function reportCaseDoorError(fnName, error) {
  try { console.error(CASE_DOOR_FAILURE_TAG + " " + describeCaseDoorError(fnName, error)); } catch (e) {}
  try {
    const msg = caseDoorFailureText(fnName, error);
    const now = Date.now();
    if (msg !== _lastCaseDoorAlert.msg || now - _lastCaseDoorAlert.at > 6000) {
      _lastCaseDoorAlert = { msg, at: now };
      notifyWriteFailed(msg);
    }
  } catch (e) {}
}

// What the raise door is handed. PURE and exported so the node selftest can pin
// the shape without a database — same convention as the planners above.
//
// The fields it deliberately does NOT send are the point of the door: no
// `site_id` (the database resolves the shop from its key, so a tablet cannot
// hand in an id it should not know — note that it DOES still name the shop, by
// key, and that is not caller isolation: see 1440's header and Lane CB), no
// `status` (forced open), no `raised_by` (0009's trigger takes it from the staff
// pass), no `source`, and never a PIN — the pass is already on the session, and
// a PIN in a payload is the exact mistake the tile-feed leak taught this estate
// not to repeat.
export function buildCaseDoorPayload({ siteKey, kind, caseRef, description, detail, testMode, entryDate }) {
  return {
    site_key: siteKey,
    kind,
    legacy_ref: caseRef || null,
    description: description || null,
    detail: detail || {},
    test_mode: !!testMode,
    entry_date: entryDate,
    // byte-for-byte the key the direct insert uses, so a tablet that raised an
    // incident before the flip and retries after it still collapses onto the
    // same row instead of opening a second one.
    idem_key: qaKey(testMode, siteKey, "case", kind, caseRef || idem(entryDate, description)),
  };
}
async function caseIdByRef(caseRef) {
  if (!caseRef) return null;
  if (caseDoorEnabled()) {
    // ON: ask the database for the id. Same answer, no read of the table.
    const { data, error } = await supabase.rpc("compliance_case_id_by_ref", { p_ref: caseRef });
    if (error) { reportCaseDoorError("compliance_case_id_by_ref", error); return null; }
    return data || null;
  }
  const { data } = await supabase.from("compliance_cases").select("id").eq("legacy_ref", caseRef).maybeSingle();
  return data?.id || null;
}

// Open (or find) a case. A retried raise re-sends the same legacy_ref and
// finds the existing row; the unique index is the backstop if two raises race.
async function ensureCase({ siteId, siteKey, kind, caseRef, description, detail, testMode, entryDate }) {
  if (caseDoorEnabled()) {
    // ON: one call does the existing-check AND the insert and hands back the id.
    // The door resolves the shop from its key, forces the case open, and is
    // idempotent on the incident id, so a retried raise still returns the first
    // case rather than making a second one. `siteId` is deliberately not sent —
    // the database resolves the shop from its KEY instead, so a tablet cannot
    // hand in a raw id. It does still NAME the shop by key: a tablet is only
    // stopped from naming somebody else's shop once it has been BOUND to one
    // (Lane CB's 1460 stamps a `site_key` claim and 1440's doors refuse any
    // other shop when it is present). No tablet is bound today.
    const { data, error } = await supabase.rpc("compliance_case_ensure", {
      p: buildCaseDoorPayload({ siteKey, kind, caseRef, description, detail, testMode, entryDate }),
    });
    if (error) { reportCaseDoorError("compliance_case_ensure", error); return null; }
    return data?.id || null;
  }
  const existing = await caseIdByRef(caseRef);
  if (existing) return existing;
  const { data, error } = await supabase.from("compliance_cases")
    .insert({
      site_id: siteId, kind, status: "open", opened_date: entryDate,
      description: description || null, detail: detail || {},
      legacy_ref: caseRef || null, test_mode: !!testMode,
      idem_key: qaKey(testMode, siteKey, "case", kind, caseRef || idem(entryDate, description)),
    })
    .select("id").single();
  if (error) return await caseIdByRef(caseRef); // lost a race / retried — link to the winner
  return data?.id || null;
}

async function resolveCaseByRef(caseRef, resolution) {
  if (!caseRef) return;
  if (caseDoorEnabled()) {
    // ON: the close goes through the door too. This is the one that would fail
    // SILENTLY under 1441 — a plain UPDATE whose WHERE reads the table's own
    // columns matches nothing once the read is locked, and nothing here checks
    // the result, so every closed incident would sit "open" for ever.
    const { error } = await supabase.rpc("compliance_case_resolve_by_ref", {
      p: { legacy_ref: caseRef, resolution: resolution || null },
    });
    if (error) reportCaseDoorError("compliance_case_resolve_by_ref", error);
    return;
  }
  await supabase.from("compliance_cases")
    .update({ status: "resolved", resolution: resolution || null })
    .eq("legacy_ref", caseRef).eq("status", "open");
}

// --- the three recorders the wiring calls ----------------------------------------
// Call from the .then() of the old backend fetch, passing its response:
//   complianceSync.recordComplianceLog({ siteKey, event, payload, staffPin,
//     testMode: cmpIsTestMode(), response: d })
export function recordComplianceLog({ siteKey, event, payload = {}, staffPin, testMode = false, response = null }) {
  const plan = planComplianceRow({ siteKey, event, payload, testMode, response });
  return shadowWrite(`${event} ${siteKey}`, { staffPin, siteKey }, async (siteId) => {
    let caseId = null;
    if (plan.casePlan) {
      caseId = plan.casePlan.op === "open"
        ? await ensureCase({ siteId, siteKey, kind: plan.casePlan.kind, caseRef: plan.casePlan.caseRef, description: plan.casePlan.description, detail: plan.casePlan.detail, testMode, entryDate: plan.entryDate })
        : await caseIdByRef(plan.casePlan.caseRef); // follow/resolve: link if the case is known
    }
    const res = await supabase.from("compliance_events").insert({
      site_id: siteId, event, entry_date: plan.entryDate, case_id: caseId,
      payload: payload || {}, test_mode: !!testMode,
      legacy_ref: plan.legacyRef, idem_key: plan.idemKey,
    });
    if (!res.error) {
      // post-steps only after a clean event insert, so a collapsed retry
      // doesn't redo them (both are idempotent anyway).
      if (plan.casePlan?.op === "resolve") await resolveCaseByRef(plan.casePlan.caseRef, plan.casePlan.resolution);
      if (plan.autoCorrective) {
        // the old backend auto-raised a corrective off this record (pest
        // yes-row, out-of-tolerance probe, failed fish batch, fridge raise) —
        // mirror it so the case reconcile matches.
        await ensureCase({ siteId, siteKey, kind: "corrective", caseRef: plan.autoCorrective.caseRef, description: plan.autoCorrective.description, detail: { source: event }, testMode, entryDate: plan.entryDate });
      }
    }
    return res;
  });
}

export function recordHaccpEvent({ siteKey, section, field, value, extra = null, date = null, staffPin, testMode = false }) {
  const plan = planHaccpRow({ siteKey, section, field, value, testMode, date });
  return shadowWrite(`haccp ${section}/${field} ${siteKey}`, { staffPin, siteKey }, (siteId) =>
    supabase.from("haccp_events").insert({
      site_id: siteId, entry_date: plan.entryDate, section, field: String(field),
      value: value === undefined ? true : value, extra,
      test_mode: !!testMode, idem_key: plan.idemKey,
    }));
}

// The chef's day sign-off — one live signature per site per day is enforced by
// the DB (0009 partial unique index, QA counted apart); a duplicate sign shadow
// lands as a rejected insert, logged and swallowed.
export function recordHaccpSign({ siteKey, date = null, staffPin, testMode = false }) {
  const entryDate = date || londonToday();
  return shadowWrite(`haccp sign ${siteKey}`, { staffPin, siteKey }, (siteId) =>
    supabase.from("haccp_days").insert({
      site_id: siteId, entry_date: entryDate, test_mode: !!testMode,
      idem_key: qaKey(testMode, siteKey, "haccpsign", entryDate),
    }));
}
